/** Formatting helpers. All amounts go through Intl.NumberFormat. */

const amountFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

const rateFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 6,
});

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** "1234.5" -> "1,234.50". Returns "—" for missing/invalid input. */
export function formatAmount(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return "—";
  return amountFormatter.format(num);
}

/** Exchange rate with 4–6 decimals. Returns "—" for invalid input. */
export function formatRate(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return "—";
  return rateFormatter.format(num);
}

/** ISO timestamp -> "Aug 15, 2026, 21:04". Returns "—" for invalid input. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  return dateFormatter.format(new Date(ms));
}

const intFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

/** 12345 -> "12,345". Returns "—" for missing/invalid input. */
export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return intFormatter.format(value);
}

const usdCompactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const usd2Formatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** 1234567 -> "1.2M". Returns "—" for missing/invalid input. */
export function formatUsdCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return usdCompactFormatter.format(value);
}

/** 1234.5 -> "1,234.50" (always 2 decimals). Returns "—" for invalid input. */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return usd2Formatter.format(value);
}

/**
 * Coarse "time ago": "just now", "3 min ago", "2 h ago", "5 d ago"; the exact
 * instant goes in the element's `title`. A future time (clock skew) reads "just
 * now", never a negative age. "—" for missing or invalid input.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  const seconds = Math.floor((now - ms) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** "0x1234…abcd" style truncation for hashes/addresses. */
export function shortHash(hash: string, chars = 4): string {
  if (hash.length <= 2 + chars * 2) return hash;
  return `${hash.slice(0, 2 + chars)}…${hash.slice(-chars)}`;
}

/** Seconds until an ISO deadline, floored at 0. */
export function secondsUntil(iso: string, now: number = Date.now()): number {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return 0;
  return Math.max(0, Math.floor((ms - now) / 1000));
}

/** 95 -> "1:35", 7 -> "0:07". */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

// ---- dates as the Maker portal's tables write them ----------------------------

const dayFormatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const dayYearFormatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const clockFormatter = new Intl.DateTimeFormat("en-US", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
const utcDayFormatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const utcClockFormatter = new Intl.DateTimeFormat("en-US", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "UTC" });

function parse(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === "") return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : new Date(ms);
}

const sameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "Oct 11", with the year when it is not this year's. "—" for missing input. */
export function formatDay(iso: string | null | undefined, now: number = Date.now()): string {
  const d = parse(iso);
  if (d === null) return "—";
  return d.getFullYear() === new Date(now).getFullYear() ? dayFormatter.format(d) : dayYearFormatter.format(d);
}

/** "13:58", local time. */
export function formatClock(iso: string | null | undefined): string {
  const d = parse(iso);
  return d === null ? "—" : clockFormatter.format(d);
}

/**
 * "Today, 09:12" or "Oct 10, 17:40" (with the year when not this year's),
 * local time. `today: false` keeps the date for today as well ("Oct 11, 10:36"),
 * as a record of when something was made reads.
 */
export function formatDayTime(iso: string | null | undefined, now: number = Date.now(), opts: { today?: boolean } = {}): string {
  const d = parse(iso);
  if (d === null) return "—";
  if (opts.today !== false && sameLocalDay(d, new Date(now))) return `Today, ${clockFormatter.format(d)}`;
  return `${formatDay(iso, now)}, ${clockFormatter.format(d)}`;
}

/** "Kestrel Markets'" and "AileFX Desk's": the possessive of a name. */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

/** "Oct 11, 13:52" in UTC, for tables headed "Time, UTC". */
export function formatUtcDayTime(iso: string | null | undefined): string {
  const d = parse(iso);
  return d === null ? "—" : `${utcDayFormatter.format(d)}, ${utcClockFormatter.format(d)}`;
}

/** "0xc0de…0002": an address short enough for a table cell. */
export function shortAddress(address: string): string {
  return shortHash(address, 4);
}
